const DB_NAME = 'VideoReelsDB';
const DB_VERSION = 1;
const STORE_NAME = 'videos';

/**
 * Opens the IndexedDB database.
 * @returns {Promise<IDBDatabase>}
 */
export const openDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = (event) => {
            console.error('IndexedDB error:', event.target.error);
            reject('Could not open database');
        };

        request.onsuccess = (event) => {
            resolve(event.target.result);
        };

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
            }
        };
    });
};

/**
 * Saves a video file to IndexedDB.
 * @param {File} file - The video file to save.
 * @param {string} title - The title of the video.
 * @returns {Promise<Object>} - The saved video object with ID.
 */
export const saveVideo = async (file, title) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);

        const videoData = {
            title: title || file.name,
            blob: file,
            filename: file.name,
            createdAt: new Date().toISOString()
        };

        const request = store.add(videoData);

        request.onsuccess = (event) => {
            resolve({ ...videoData, id: event.target.result });
        };

        request.onerror = (event) => {
            console.error('Error saving video:', event.target.error);
            reject('Could not save video');
        };
    });
};

/**
 * Retrieves all videos from IndexedDB.
 * @returns {Promise<Array>} - Array of video objects.
 */
export const getVideos = async () => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = (event) => {
            resolve(event.target.result);
        };

        request.onerror = (event) => {
            console.error('Error fetching videos:', event.target.error);
            reject('Could not fetch videos');
        };
    });
};

/**
 * Deletes a video from IndexedDB by ID.
 * @param {number} id - The ID of the video to delete.
 * @returns {Promise<void>}
 */
export const deleteVideo = async (id) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(id);

        request.onsuccess = () => {
            resolve();
        };

        request.onerror = (event) => {
            console.error('Error deleting video:', event.target.error);
            reject('Could not delete video');
        };
    });
};
